import { useCallback, useMemo, useState } from 'react';

/**
 * Excel-style column filtering over an already-loaded row set (the "AutoFilter" this app
 * replaced, rebuilt as something that can be checked).
 *
 * Deliberately NOT persisted anywhere — see ColumnFilter.tsx. A filter left on from a previous
 * visit that silently shortens the next return opened is exactly the failure mode this page was
 * built to get rid of.
 */

export interface ColumnFilterState {
    /** Values the row's column must match, as strings. `null` = the checkbox list imposes no restriction. */
    selected: Set<string> | null;
    /** Numeric range — applies only where the column is numeric. `null`/`undefined` = unbounded. */
    min?: number | null;
    max?: number | null;
}

export interface FilterColumn<Row> {
    key: string;
    accessor: (row: Row) => string | number | null | undefined;
    numeric?: boolean;
}

export function isColumnFilterActive(state: ColumnFilterState | undefined): boolean {
    return !!state && (state.selected !== null || state.min != null || state.max != null);
}

export function useColumnFilters<Row>(rows: Row[], columns: FilterColumn<Row>[]) {
    const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});

    // A row passes column `exceptKey`'s OWN filter unconditionally, so this doubles as both the
    // "final filtered set" (exceptKey = null) and, per column, "what would the other filters
    // leave standing" — which is what the popover needs to offer the right checkbox list.
    const passesExcept = useCallback((row: Row, exceptKey: string | null) => {
        for (const col of columns) {
            if (col.key === exceptKey) continue;
            const f = filters[col.key];
            if (!f) continue;
            const raw = col.accessor(row);
            if (f.min != null || f.max != null) {
                const n = typeof raw === 'number' ? raw : null;
                if (n === null) return false;
                if (f.min != null && n < f.min) return false;
                if (f.max != null && n > f.max) return false;
            }
            if (f.selected) {
                const s = raw === null || raw === undefined ? '' : String(raw);
                if (!f.selected.has(s)) return false;
            }
        }
        return true;
    }, [columns, filters]);

    const filtered = useMemo(
        () => rows.filter(r => passesExcept(r, null)),
        [rows, passesExcept]
    );

    /**
     * Unique values for one column's popover, drawn from rows that already pass every OTHER
     * column's filter — narrowing one column narrows what the next column's list offers, same
     * as Excel's AutoFilter.
     */
    const uniqueValuesFor = useCallback((key: string) => {
        const col = columns.find(c => c.key === key);
        if (!col) return [];
        const seen = new Set<string>();
        for (const r of rows) {
            if (!passesExcept(r, key)) continue;
            const raw = col.accessor(r);
            seen.add(raw === null || raw === undefined ? '' : String(raw));
        }
        return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [rows, columns, passesExcept]);

    const setColumnFilter = useCallback((key: string, state: ColumnFilterState | null) => {
        setFilters(f => {
            if (state === null) {
                if (!(key in f)) return f;
                const next = { ...f };
                delete next[key];
                return next;
            }
            return { ...f, [key]: state };
        });
    }, []);

    const isFiltered = useCallback((key: string) => key in filters, [filters]);
    const activeCount = Object.keys(filters).length;
    const clearAll = useCallback(() => setFilters({}), []);

    return { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor };
}
